import styles from './index.module.scss'
import { Button, Upload, message, Select } from "antd"
import { useCallback, useMemo, useContext, useState, useRef, useEffect } from "react"
import Utils from '@utils/utils';
import { IMAGE_TYPE, GENERATE_MODE } from '@utils/CONST';
import { AppContext } from '@utils/AppContext';
import Iconfont from '@utils/Iconfont';
import API from "@api/api";
import { GenerateContext } from '../../../hooks/GenerateContext'
import { workflowOfGenerate } from '@utils/workflowOfBuildTaskV2'
import PromptIcon from '@assets/images/svgIcon/promptIcon.svg'

const UNSUPPORTED_FILE_MESSAGE = '您只能上传JPG/PNG/WEBP/AVIF格式的文件';
const getUploadErrorMessage = (reasonList = []) => {
    const uniqueReasons = [...new Set((reasonList || []).filter(Boolean))]
    if (!uniqueReasons.length) return '图片不符合要求';
    return `${uniqueReasons.join('，')}`;
}


const UploadInput = (props) => {
    const { setSelectedTaskListId } = useContext(AppContext);
    const { modelImage, containerRef } = props
    const generateContext = useContext(GenerateContext)
    const { setProjectId, generateNum, setGenerateNum, prompt, setPrompt, matchImages, setMatchImages, generateMode, setGenerateMode } = generateContext
    const [uploadFileList, setUploadFileList] = useState([])
    const [errorList, setErrorList] = useState([])
    const [promptLoading, setPromptLoading] = useState(false)
    const [generateLoading, setGenerateLoading] = useState(false)
    const [showPlaceholder, setShowPlaceholder] = useState(!prompt)
    const [floatingStyle, setFloatingStyle] = useState()
    const isMounted = useRef(true)
    const lastPromptRequestId = useRef(0)

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    useEffect(() => {
        const updateFloatingStyle = () => {
            const container = containerRef?.current
            if (!container) return

            const rect = container.getBoundingClientRect()
            const nextWidth = Math.min(Math.max(rect.width - 48, 0), 1152)

            setFloatingStyle({
                left: `${rect.left + rect.width / 2}px`,
                width: `${nextWidth}px`,
            })
        }

        updateFloatingStyle()
        window.addEventListener('resize', updateFloatingStyle)

        return () => {
            window.removeEventListener('resize', updateFloatingStyle)
        }
    }, [containerRef])

    // 停止上传
    const uploadAbort = useRef(false)
    const handleUploadAbort = () => {
        uploadAbort.current = true
    }
    // 正在上传的图片
    const remainUploadLength = useMemo(() => {
        return matchImages.length + errorList.length
    }, [uploadFileList, matchImages, errorList])

    const lastUploadBatchKeyRef = useRef("")
    const inFlightBatchKeyRef = useRef("")
    const beforeUploadTimerRef = useRef(null)
    const pendingBeforeUploadFilesRef = useRef([])
    const pendingDropErrorReasonsRef = useRef([])
    const pendingDropErrorTimerRef = useRef(null)

    const getUploadBatchKey = (files) => {
        return (Array.from(files || [])).map((f) => `${f?.name || ""}-${f?.size || ""}-${f?.lastModified || ""}-${f?.type || ""}`).join("|")
    }

    const isFileDragEvent = (e) => {
        const types = e?.dataTransfer?.types
        return Array.isArray(types) ? types.includes("Files") : types?.contains?.("Files")
    }

    const isSupportedImageFile = (file) => {
        const fileType = (file?.type || '').toLowerCase()
        const fileName = file?.name || ''
        return ['image/jpeg', 'image/png', 'image/webp', 'image/avif'].includes(fileType)
            || /\.(jpe?g|png|webp|avif)$/i.test(fileName)
    }

    const beforeUpload = async (file, fileList) => {
        const files = Array.from(fileList || [])
        if (!files.length) return false

        // antd 拖拽时 beforeUpload 可能会多次触发（fileList 先是子集再是全集），
        // 这里做一次短延迟合并，仅处理最后一批，避免重复提示。
        pendingBeforeUploadFilesRef.current = files
        if (beforeUploadTimerRef.current) clearTimeout(beforeUploadTimerRef.current)
        beforeUploadTimerRef.current = setTimeout(() => {
            const finalFiles = Array.from(pendingBeforeUploadFilesRef.current || [])
            if (!finalFiles.length) return
            const batchKey = getUploadBatchKey(finalFiles)
            if (lastUploadBatchKeyRef.current === batchKey || inFlightBatchKeyRef.current === batchKey) return
            const initialErrorReasons = [...pendingDropErrorReasonsRef.current]
            pendingDropErrorReasonsRef.current = []
            if (pendingDropErrorTimerRef.current) clearTimeout(pendingDropErrorTimerRef.current)
            lastUploadBatchKeyRef.current = batchKey
            handleUpload(finalFiles, batchKey, initialErrorReasons)
        }, 50)
        return false
    }

    useEffect(() => {
        return () => {
            if (beforeUploadTimerRef.current) clearTimeout(beforeUploadTimerRef.current)
            if (pendingDropErrorTimerRef.current) clearTimeout(pendingDropErrorTimerRef.current)
        }
    }, [])

    const handleDropCapture = (e) => {
        if (!isFileDragEvent(e)) return
        const itemFiles = Array.from(e.dataTransfer?.items || [])
            .filter((item) => item?.kind === 'file')
            .map((item) => item.getAsFile?.())
            .filter(Boolean)
        const droppedFiles = itemFiles.length > 0 ? itemFiles : Array.from(e.dataTransfer?.files || [])
        if (!droppedFiles.length) return
        const unsupportedFiles = droppedFiles.filter((file) => !isSupportedImageFile(file))
        pendingDropErrorReasonsRef.current = unsupportedFiles.length > 0 ? [UNSUPPORTED_FILE_MESSAGE] : []
        if (pendingDropErrorTimerRef.current) clearTimeout(pendingDropErrorTimerRef.current)
        if (unsupportedFiles.length === droppedFiles.length && unsupportedFiles.length > 0) {
            pendingDropErrorTimerRef.current = setTimeout(() => {
                if (pendingDropErrorReasonsRef.current.length > 0) {
                    message.error(getUploadErrorMessage(pendingDropErrorReasonsRef.current), 5)
                    pendingDropErrorReasonsRef.current = []
                }
            }, 120)
        }
    }

    const handleUpload = async (fileList, batchKey = "", initialErrorReasons = []) => {
        if (batchKey && inFlightBatchKeyRef.current === batchKey) return
        if (batchKey) inFlightBatchKeyRef.current = batchKey
        console.log('fileList', fileList)
        if (fileList.length >= 10) message.warning('最多可上传10张图')
        const errorList = []
        const errorReasonList = [...initialErrorReasons]
        const nextSuccessList = [...matchImages]
        setErrorList([...errorList])
        setMatchImages([...nextSuccessList])

        const pushUploadError = (file, reason) => {
            errorList.push(file)
            if (reason) errorReasonList.push(reason)
            setErrorList([...errorList])
        }

        try {

            fileList = [...fileList].slice(0, 10 - nextSuccessList.length)
            setUploadFileList(fileList)

            for (let file of fileList) {
                if (uploadAbort.current) { uploadAbort.current = false; break; }
                try {
                    await Utils.validateImageFile(file, true)
                } catch (err) {
                    pushUploadError(file, err?.message)
                    continue
                }
                if (uploadAbort.current) { uploadAbort.current = false; break; }

                let resizeRes = await Utils.resizeImageFileMaxByAliyunBy(file, 4096)

                if (!resizeRes || !resizeRes.success) {
                    pushUploadError(file, resizeRes?.message || '图片处理失败，请更换图片后重试')
                    continue
                } else file = resizeRes.file
                if (uploadAbort.current) { uploadAbort.current = false; break; }

                // 上传图片
                const res = await Utils.checkImageIslegal(file, 0, IMAGE_TYPE.FUSION_WEAR)
                if (!res || !res.isLegal) {
                    pushUploadError(file, '图片内容不符合要求，请更换后重试')
                    continue
                }
                if (uploadAbort.current) { uploadAbort.current = false; break; }

                const url = res.data
                nextSuccessList.push({ url })
                setMatchImages([...nextSuccessList])
            }
        } catch (err) {
            message.warning(err.message)
        } finally {
            setErrorList([...errorList])
            setMatchImages([...nextSuccessList])
            if (errorList.length > 0 || errorReasonList.length > 0) {
                message.error(getUploadErrorMessage(errorReasonList), 5)
            }
            if (batchKey) inFlightBatchKeyRef.current = ""
        }
    }

    const handleDelete = (index) => {
        const nextSuccessList = [...matchImages]
        nextSuccessList.splice(index, 1)
        setMatchImages([...nextSuccessList])
    }


    // 输入框 ------------------------
    const contentEditableRef = useRef(null)
    const KEYWORDS = useMemo(() => {
        const kws = ['模特', ...Array.from({ length: 10 }, (_, i) => `图${i + 1}`)];
        return kws.sort((a, b) => b.length - a.length);
    }, [])

    const getInputValue = useCallback(() => {
        if (!contentEditableRef.current) return "";
        const text = contentEditableRef.current.innerText || "";
        const cleanText = text.replace(/[【】]/g, '');
        const pattern = new RegExp(`(${KEYWORDS.join('|')})`, 'g');
        return cleanText.replace(pattern, '【$1】');
    }, [KEYWORDS])


    const saveSelection = (containerEl) => {
        const selection = window.getSelection();
        if (selection.rangeCount === 0) return null;
        const range = selection.getRangeAt(0);
        const preSelectionRange = range.cloneRange();
        preSelectionRange.selectNodeContents(containerEl);
        preSelectionRange.setEnd(range.startContainer, range.startOffset);
        const start = preSelectionRange.toString().length;
        return {
            start: start,
            end: start + range.toString().length
        };
    };

    const restoreSelection = (containerEl, savedSelection) => {
        if (!savedSelection) return;
        let charIndex = 0, range = document.createRange();
        range.setStart(containerEl, 0);
        range.collapse(true);
        let nodeStack = [containerEl], node, foundStart = false, stop = false;

        while (!stop && (node = nodeStack.pop())) {
            if (node.nodeType === 3) {
                const nextCharIndex = charIndex + node.length;
                if (!foundStart && savedSelection.start >= charIndex && savedSelection.start <= nextCharIndex) {
                    range.setStart(node, savedSelection.start - charIndex);
                    foundStart = true;
                }
                if (foundStart && savedSelection.end >= charIndex && savedSelection.end <= nextCharIndex) {
                    range.setEnd(node, savedSelection.end - charIndex);
                    stop = true;
                }
                charIndex = nextCharIndex;
            } else {
                let i = node.childNodes.length;
                while (i--) {
                    nodeStack.push(node.childNodes[i]);
                }
            }
        }
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
    };

    const formatText = useCallback((text) => {
        const cleanText = (text || '').replace(/[【】]/g, '');
        const pattern = new RegExp(`(${KEYWORDS.join('|')})`, 'g');
        return cleanText.replace(pattern, '<span class="keyword-highlight">$1</span>');
    }, [KEYWORDS])

    const adjustContentEditableHeight = useCallback(() => {
        const el = contentEditableRef.current
        if (!el) return

        const computedStyle = window.getComputedStyle(el)
        const lineHeight = parseFloat(computedStyle.lineHeight) || 21
        const paddingTop = parseFloat(computedStyle.paddingTop) || 0
        const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0
        const borderTop = parseFloat(computedStyle.borderTopWidth) || 0
        const borderBottom = parseFloat(computedStyle.borderBottomWidth) || 0
        const verticalExtra = paddingTop + paddingBottom + borderTop + borderBottom
        const minHeight = lineHeight * 2 + verticalExtra
        const maxHeight = lineHeight * 4 + verticalExtra

        el.style.height = `${minHeight}px`
        const nextHeight = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight)
        el.style.height = `${nextHeight}px`
    }, [])

    const isComposing = useRef(false)

    const handleContentInput = useCallback((e) => {
        let text = e.target.innerText;
        const isEmpty = text.replace(/\n/g, '').length === 0;
        setShowPlaceholder(isEmpty);

        if (isComposing.current) return;

        // 限制800字
        if (text.length > 800) {
            text = text.slice(0, 800);
            message.warning("最多可输入800字");
        }

        // 处理清空的情况，防止留有 <br> 或 \n 导致 placeholder 不显示
        if (isEmpty) {
            if (e.target.innerHTML !== "") {
                e.target.innerHTML = "";
                // 恢复光标到起始位置
                const range = document.createRange();
                range.setStart(e.target, 0);
                range.collapse(true);
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(range);
            }
            requestAnimationFrame(adjustContentEditableHeight)
            return;
        }

        const selection = saveSelection(e.target);
        const newHTML = formatText(text);

        if (e.target.innerHTML !== newHTML) {
            e.target.innerHTML = newHTML;
            restoreSelection(e.target, selection);
        }
        requestAnimationFrame(adjustContentEditableHeight)
    }, [adjustContentEditableHeight, formatText])

    const handleCompositionStart = () => {
        isComposing.current = true;
    }

    const handleCompositionEnd = (e) => {
        isComposing.current = false;
        handleContentInput(e);
    }

    const editableDiv = useMemo(() => (
        <div
            ref={contentEditableRef}
            className={styles.contenteditableWrap}
            contentEditable
            onInput={handleContentInput}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            suppressContentEditableWarning
        />
    ), [handleContentInput])

    useEffect(() => {
        if (contentEditableRef.current) {
            const initialText = prompt || "";
            contentEditableRef.current.innerHTML = formatText(initialText);
            setShowPlaceholder(initialText.length === 0)
            requestAnimationFrame(adjustContentEditableHeight)
        }
    }, [adjustContentEditableHeight, formatText, prompt])

    // 获取搭配上身提示词
    const getAlgoPrompt = () => {
        const requestId = ++lastPromptRequestId.current
        setPromptLoading(true)
        setShowPlaceholder(false)
        API.getAlgoPrompt({
            images: matchImages.map(item => item.url),
            modelImage: modelImage
        }).then(res => {
            if (requestId !== lastPromptRequestId.current) return
            if (res?.code == 0) {
                setPrompt(res.data?.[0] || '')
                if (contentEditableRef.current) contentEditableRef.current.innerHTML = formatText(res.data?.[0] || '')
            } else {
                throw new Error(res.message)
            }
        }).catch(err => {
            if (requestId !== lastPromptRequestId.current) return
            message.error(err.message)
        })
            .finally(() => {
                if (requestId === lastPromptRequestId.current) {
                    setPromptLoading(false)
                }
            })
    }

    // 清空选项
    useEffect(() => {
        if (matchImages.length == 0 && !modelImage) {
            setPrompt('')
            setProjectId('')
            if (contentEditableRef.current) contentEditableRef.current.innerHTML = ''
            lastPromptRequestId.current = 0
        }
        if (matchImages.length > 0 || modelImage) {
            // getAlgoPrompt()
        }
    }, [matchImages, modelImage])

    // 生成搭配上身
    const handleGenerate = async () => {
        const prompt = await getInputValue()
        setPrompt(prompt)

        try {
            // 需要登录
            const status = Utils.checkLoginStatus(() => handleGenerate())
            if (!status) return

            setGenerateLoading(true)
            const currentGenerateContext = {
                ...generateContext,
                prompt: prompt
            }
            const projectId = await workflowOfGenerate(IMAGE_TYPE.FUSION_WEAR, currentGenerateContext, {
                projectId: generateContext.projectId
            })
            if (!projectId) return
            setSelectedTaskListId(projectId)
            setProjectId(projectId)
            message.success("生成中，请稍候")
            props.createClick()
        } catch (err) {
            console.log(err)
            message.warning(err.message)
        } finally {
            setGenerateLoading(false)
        }
    }

    const uploadAfterWrap = (
        <div className={styles.successUploadWrap}>
            <div className={styles.imgList}>
                {
                    matchImages.map((item, index) => (
                        <div className={styles.imgItem} key={item.url}>
                            <img src={Utils.getImgThumbnail(item.url)} alt="" />
                            <span className={styles.imgName}>{`图${index + 1}`}</span>
                            <div className={styles.deleteIcon} onClick={() => handleDelete(index)}>
                                <Iconfont className={styles.Icon} type="icon-DeleteOutlined" />
                            </div>
                        </div>
                    ))
                }
                {
                    modelImage &&
                    <div className={styles.imgItem}>
                        <img src={Utils.getImgThumbnail(modelImage)} alt="" />
                        <span className={styles.imgName}>模特</span>
                        <div className={styles.deleteIcon} onClick={() => props.setModelImage([])}>
                            <Iconfont className={styles.Icon} type="icon-DeleteOutlined" />
                        </div>
                    </div>
                }
                {
                    matchImages.length < 10 &&
                    <div className={styles.uploadItem}>
                        <Upload
                            accept='image/jpeg,image/png,image/webp,image/avif'
                            listType="picture-card"
                            showUploadList={false}
                            multiple
                            beforeUpload={beforeUpload}
                        >
                            <Iconfont className={styles.Icon} type="icon-general_dd" />
                        </Upload>
                    </div>
                }
                {
                    !modelImage &&
                    <div
                        className={[styles.uploadItem, styles.selectModelItem].join(" ")}
                        onClick={props.handleOpenDrawer}
                    >
                        <div className={styles.selectModelInner}>
                            <span className={styles.selectModelPlus}>+</span>
                            <span className={styles.selectModelText}>模特</span>
                        </div>
                    </div>
                }
            </div>
        </div>
    )

    return (
        <div className={styles.UploadInput} style={floatingStyle}>
            {
                matchImages.length > 0 || modelImage ? <>
                    {uploadAfterWrap}
                    <div className={styles.inputContainer}>
                        {showPlaceholder && (
                            <div className={styles.placeholderOverlay}>
                                请描述想要生成的画面，或点击
                                <span onClick={getAlgoPrompt} className={styles.smartPromptLink}>智能提示词</span>
                                自动生成
                            </div>
                        )}
                        {editableDiv}
                    </div>
                    <div className={styles.uploadParams}>
                        {/* {
                            uploading && <span className={styles.uploadNum}><LoadingOutlined style={{ marginRight: 8 }} /> 已上传{currentBatchUploadedCount} / 共{uploadFileList.length}张</span>
                        } */}
                        <div className={styles.rightGroupsBtn}>
                            <Button
                                loading={promptLoading}
                                className={styles.smartPromptBtn}
                                onClick={getAlgoPrompt}
                            >
                                {!promptLoading && <img style={{ width: 16 }} src={PromptIcon} alt="" />}
                                智能提示词
                            </Button>
                            <Select
                                defaultValue={generateMode}
                                className={styles.borderlessSelect}
                                style={{ width: 115, height: 32 }}
                                onChange={(value) => setGenerateMode(value)}
                                options={[{
                                    value: GENERATE_MODE.STD,
                                    label: '标准模式',
                                }, {
                                    value: GENERATE_MODE.HQ,
                                    label: '高品质模式',
                                }]}
                            />
                            <Select
                                defaultValue={generateNum}
                                className={styles.borderlessSelect}
                                style={{ width: 100, height: 32 }}
                                onChange={(value) => setGenerateNum(value)}
                                options={[1, 2, 3, 4].map(n => ({
                                    value: n,
                                    label: `${n}张`,
                                }))}
                            />
                            <Button
                                onClick={() => handleGenerate()}
                                disabled={matchImages.length == 0}
                                className={styles.generateBtn}
                                type="primary"
                                loading={generateLoading}>
                                生成 <img style={{ width: 16 }} src={require('@assets/images/arcbean3.png')} />
                                {(generateMode === GENERATE_MODE.STD ? 10 : 15) * generateNum}
                            </Button>
                        </div>
                    </div>
                </> :
                    <div onDropCapture={handleDropCapture}>
                        <Upload.Dragger
                            className={styles.Uploader}
                            accept='image/jpeg,image/png,image/webp,image/avif'
                            listType="picture-card"
                            showUploadList={false}
                            multiple
                            beforeUpload={beforeUpload}
                        >
                            <div className={styles.UploadInputContent}>
                                <img src={"https://photostudio-ai-oss.arcsoft.com.cn/photostudio-web/admin/images_v3/FusionWearUploadIcon.png"} alt="" />
                                <Button type="primary" className={styles.uploadBtn}>本地上传</Button>
                                <p>点击按钮，或将图片拖拽至此上传穿搭商品图，最多支持10件商品</p>
                            </div>
                        </Upload.Dragger>
                    </div>
            }

            {/* {
                uploading && <div className={styles.uploadNumber}>
                    <p><span>{remainUploadLength} / {uploadFileList.length} 正在上传</span> <span onClick={handleUploadAbort}>停止</span></p>
                    <Progress percent={Math.ceil(remainUploadLength / uploadFileList.length * 100)} showInfo={false} />
                </div>
            } */}
        </div>
    )
}

export default UploadInput




